import type { OrgUnit } from '@/types/OrgUnit';
import type { HireJobPrefill } from '@/types/employee';
import { matchOrgUnitByName } from '@/utils/orgUnitMatch';

/**
 * The Job Title value standing for a designation that does not exist yet:
 * the hire's job title, created when the employee is saved.
 */
export const NEW_DESIGNATION_VALUE = 'new-designation-from-job';

type OrgUnitLike = Pick<OrgUnit, 'id' | 'name'> &
  Partial<Pick<OrgUnit, 'status' | 'deletedAt' | 'branchMappings'>>;

export type DesignationPrefill =
  | { kind: 'existing'; id: string; name: string }
  | { kind: 'new'; name: string };

/** What the Job Title and Department pickers would offer for this branch. */
const isOffered = (unit: OrgUnitLike, branchId: string | null | undefined) =>
  unit.status !== false &&
  !unit.deletedAt &&
  (!branchId ||
    !!unit.branchMappings?.some((mapping) => mapping.branch?.id === branchId));

/**
 * The job's title in the company's language: the French title for a French
 * company when the job has one, else the (English) title.
 */
export const hireJobTitle = (
  job: HireJobPrefill,
  companyLanguage?: string | null
) =>
  ((companyLanguage === 'fr' && job.titleFr?.trim()) || job.title)
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The Job Title a hire starts with, from the job's title (the company's
 * language first, then the other): the matching designation the picker
 * offers; else, with `canCreate`, a new one named after the job in the
 * company's language; else nothing. A name already taken by a designation
 * the picker does not offer (inactive, or another branch) is left empty,
 * since the server would refuse the duplicate.
 */
export const resolveDesignationPrefill = (
  units: readonly OrgUnitLike[],
  job: HireJobPrefill,
  branchId: string | null | undefined,
  canCreate: boolean,
  companyLanguage?: string | null
): DesignationPrefill | undefined => {
  const names =
    companyLanguage === 'fr'
      ? [job.titleFr, job.title]
      : [job.title, job.titleFr];
  const offered = matchOrgUnitByName(
    units.filter((unit) => isOffered(unit, branchId)),
    names
  );
  if (offered) return { kind: 'existing', id: offered.id, name: offered.name };
  if (matchOrgUnitByName(units, names)) return undefined;
  const name = hireJobTitle(job, companyLanguage);
  return canCreate && name ? { kind: 'new', name } : undefined;
};

/** The Department a hire starts with: a match on the job's, never a new one. */
export const resolveDepartmentPrefill = (
  units: readonly OrgUnitLike[],
  department: string | null | undefined,
  branchId: string | null | undefined
) =>
  matchOrgUnitByName(
    units.filter((unit) => isOffered(unit, branchId)),
    [department]
  );
